import React, { useContext } from "react";
import { useTheme } from "@mui/styles";
import { Box, Typography, Divider } from "@mui/material";
import ApplyButton from "../Buttons/ApplyButton";
import { ColorContext } from "../../pages/Bootcamp";
import { useLocation } from "react-router-dom";

const resize = (pxValue, baseSize) => {
  if (pxValue.split(" ").length > 1) {
    return pxValue
      .split(" ")
      .map((px) => `${parseFloat(px.replace("px", "")) * baseSize}px`)
      .join(" ");
  } else {
    return `${parseFloat(pxValue.replace("px", "")) * baseSize}px`;
  }
};

const useStyles = (theme, { mainColor, secondaryColor, colorTheme }) => ({
  container: {
    width: {
      xs: resize("459px", 0.75),
      md: resize("459px", 0.7),
      lg: resize("459px", 0.8),
      xl: resize("459px", 0.85),
    },
    height: {
      xs: resize("876px", 0.75),
      md: resize("876px", 0.7),
      lg: resize("876px", 0.8),
      xl: resize("876px", 0.85),
    },
    display: "flex",
    flexDirection: "column",
    borderRadius: {
      xs: resize("60px", 0.75),
      md: resize("60px", 0.7),
      lg: resize("60px", 0.8),
      xl: resize("60px", 0.85),
    },
    backgroundColor: theme.palette.grey.fourth,
    boxShadow: {
      xs: `${resize("5px", 0.75)} ${resize("5px", 0.75)} ${resize(
        "20px",
        0.75
      )} rgba(79, 79, 79, 0.4)`,
      md: `${resize("5px", 0.7)} ${resize("5px", 0.7)} ${resize(
        "20px",
        0.7
      )} rgba(79, 79, 79, 0.4)`,
      lg: `${resize("5px", 0.8)} ${resize("5px", 0.8)} ${resize(
        "20px",
        0.8
      )} rgba(79, 79, 79, 0.4)`,
      xl: `${resize("5px", 0.85)} ${resize("5px", 0.85)} ${resize(
        "20px",
        0.85
      )} rgba(79, 79, 79, 0.4)`,
    },
  },
  title: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    color: theme.palette.blue.main,
    fontFamily: theme.typography.montserrat.bold,
    fontSize: {
      xs: resize("35px", 0.75),
      md: resize("35px", 0.7),
      lg: resize("35px", 0.8),
      xl: resize("35px", 0.85),
    },
    minHeight: {
      xs: resize("100px", 0.75),
      md: resize("100px", 0.7),
      lg: resize("100px", 0.8),
      xl: resize("100px", 0.85),
    },
    textTransform: "uppercase",
  },
  divider: {
    minHeight: {
      xs: resize("20px", 0.75),
      md: resize("20px", 0.7),
      lg: resize("20px", 0.8),
      xl: resize("20px", 0.85),
    },
    backgroundColor: colorTheme === "blue" ? mainColor : secondaryColor,
    border: "none",
  },
  contentContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    minHeight: {
      xs: resize("221px", 0.75),
      md: resize("221px", 0.7),
      lg: resize("221px", 0.8),
      xl: resize("221px", 0.85),
    },
    padding: {
      xs: resize("24px 66px", 0.75),
      md: resize("24px 66px", 0.7),
      lg: resize("24px 66px", 0.8),
      xl: resize("24px 66px", 0.85),
    },
    backgroundColor: theme.palette.white.main,
  },
  contentText: {
    color: theme.palette.blue.main,
    fontFamily: theme.typography.montserrat.regular,
    fontSize: {
      xs: resize("25px", 0.75),
      md: resize("25px", 0.7),
      lg: resize("25px", 0.8),
      xl: resize("25px", 0.85),
    },
    textAlign: "center",
  },
  bold: {
    fontWeight: 700,
  },
  contentDivider: {
    display: "block",
    border: "none",
    borderTop: {
      xs: `dashed ${resize("1px", 0.75)}`,
      md: `dashed ${resize("1px", 0.7)}`,
      lg: `dashed ${resize("1px", 0.8)}`,
      xl: `dashed ${resize("1px", 0.85)}`,
    },
    m: {
      xs: resize("35px", 0.75),
      md: resize("35px", 0.7),
      lg: resize("35px", 0.8),
      xl: resize("35px", 0.85),
    },
  },
  pricesContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    alignItems: "center",
    padding: {
      xs: resize("60px 75px", 0.75),
      md: resize("60px 75px", 0.7),
      lg: resize("60px 75px", 0.8),
      xl: resize("60px 75px", 0.85),
    },
    flexGrow: 1,
  },
  pricesText: {
    color: theme.palette.blue.main,
    fontFamily: theme.typography.anaHeim.regular,
    fontSize: {
      xs: resize("25px", 0.75),
      md: resize("25px", 0.7),
      lg: resize("25px", 0.8),
      xl: resize("25px", 0.85),
    },
  },
  button: {
    width: "100%",
    boxShadow: "none",
    minHeight: {
      xs: resize("72px", 0.75),
      md: resize("72px", 0.7),
      lg: resize("72px", 0.8),
      xl: resize("72px", 0.85),
    },
  },
});

const FundingCard = ({
  title,
  content: { paymentDescription, installment },
  prices: { initial, financed, discount, total },
}) => {
  const theme = useTheme();
  const colors = useContext(ColorContext);
  const classes = useStyles(theme, colors);
  const { pathname } = useLocation();
  const bootcampName = pathname.split("/")[2];

  return (
    <Box sx={classes.container}>
      <Typography sx={classes.title}>{title}</Typography>
      <Divider sx={classes.divider} />
      <Box sx={classes.contentContainer}>
        <Typography sx={classes.contentText}>
          {paymentDescription[0]}
          <Typography
            component="span"
            sx={{ ...classes.contentText, ...classes.bold }}
          >
            {paymentDescription[1]}
          </Typography>
          {paymentDescription[2]}
          {installment && (
            <Divider component="span" sx={classes.contentDivider} />
          )}
          <Typography
            component="span"
            sx={{ ...classes.contentText, ...classes.bold }}
          >
            {installment}
          </Typography>
        </Typography>
      </Box>
      <Box sx={classes.pricesContainer}>
        <Typography sx={classes.pricesText}>
          {`${initial.text}: ${initial.value}`}
        </Typography>
        <Typography
          sx={classes.pricesText}
        >{`${financed.text}: ${financed.value}`}</Typography>
        <Typography sx={{ ...classes.pricesText, ...classes.bold }}>
          {`${discount.text}: ${discount.value}`}
        </Typography>
        <Typography sx={{ ...classes.pricesText, ...classes.bold }}>
          {`${total.text}: ${total.value}`}
        </Typography>
        {bootcampName === "salesforce" ? null : (
          <ApplyButton
            sx={classes.button}
            mainColor={colors.mainColor}
            secondaryColor={colors.secondaryColor}
            iconColor={colors.iconColor}
            navigateTo={`/aplica/${bootcampName}`}
          />
        )}
      </Box>
    </Box>
  );
};

export default FundingCard;
